import * as THREE from 'three';
import {
    AnglePointSnapStrategy,
    CenterPointSnapStrategy,
    DynamicRulerPointSnapStrategy,
    EndpointOriginPointSnapStrategy,
    GridPointSnapStrategy,
    HorizontalVerticalPointSnapStrategy,
    IntersectionPointSnapStrategy,
    LengthPointSnapStrategy,
    MidpointPointSnapStrategy,
    NearestPointSnapStrategy,
    ParallelPointSnapStrategy,
    PerpendicularPointSnapStrategy,
    PointHorizontalVerticalPointSnapStrategy,
    PointSnapStrategyType,
    QuadrantPointSnapStrategy,
    QuickSnapMode,
    TangentPointSnapStrategy,
    SketchCircleFamilyNearestPointProjector,
    SketchEllipseNearestPointProjector,
    SketchLineNearestPointProjector,
} from './pointSnap';
import type {
    AbstractPointSnapStrategy,
    PointSnapRequest,
    PointSnapResult,
} from './pointSnap';
import type {Viewer} from '../viewer';
import { Subject } from 'rxjs';
import type { Observable } from 'rxjs';
import {
    isAbstractSketchEdgeObject,
    isSketchArcObject,
    isSketchLineObject,
    isSketchPointObject,
} from '../mesh/sketch';
import type { SketchPointObject } from '../mesh/sketch';
import type { VisibleModelEdgeHoverOptions } from '../../types/sketch/VisibleModelEdgeProjection';
import { isSketchEntityReferencePreview } from '../reference/sketch-entity-reference/command/preview/SketchEntityReferencePreviewMarker';
import {PointerNearbyLabelPosition} from './PointerNearbyLabelManager';
import {GeometryInteractionState} from './interactionState/GeometryInteractionState';

export { QuickSnapMode } from './pointSnap/QuickSnapMode';


/**
 * 点捕捉设置接口。
 * 说明：
 * 1. 与 EditorSettingsDialog 输出的 SketchRelationsAndSnapSettings 保持结构一致；
 * 2. 定义在 cad-core 内，避免对 web-client 层产生反向依赖；
 * 3. snapOptionState 使用 Partial，因为 DynamicRuler 不在 UI 中暴露。
 */
export interface PointSnapSettings {
    /** 是否全局激活捕捉。 */
    isSketchSnapEnabled: boolean;
    /** 自动几何关系兼容字段；设置入口会把传入值统一归一化为开启。 */
    isAutoGeometryRelationsEnabled?: boolean;
    /** 是否允许捕捉模型几何体。 */
    isSnapToModelGeometryEnabled?: boolean;
    /** 是否仅在网格可见时启用网格捕捉。 */
    isGridSnapOnlyWhenGridVisibleEnabled?: boolean;
    /** 各捕捉项开关状态，键直接使用 {@link PointSnapStrategyType} 枚举值。 */
    snapOptionState: Partial<Record<PointSnapStrategyType, boolean>>;
    /** 角度捕捉角度值（度）。 */
    angleSnapDegree: number;
}

/**
 * 点捕捉管理类。
 * 职责：
 * 1. 统一注册点捕捉策略实例；
 * 2. 管理各类点捕捉策略的启用与停用状态；
 * 3. 对外提供点捕捉策略查询入口；
 * 4. 接收来自编辑器设置面板的配置并同步到各策略；
 * 5. 为后续点捕捉求解流程预留统一调度位置。
 */
export class PointSnapManager {
    /** 全局最高优先级的点类策略顺序。 */
    private static readonly TOP_PRIORITY_POINT_STRATEGIES: readonly PointSnapStrategyType[] = [
        PointSnapStrategyType.EndpointOrigin,
        PointSnapStrategyType.Center,
        PointSnapStrategyType.Quadrant,
        PointSnapStrategyType.Midpoint,
    ];

    /** 全局最高优先级点类策略集合。 */
    private static readonly TOP_PRIORITY_POINT_STRATEGY_SET: ReadonlySet<PointSnapStrategyType> = new Set(
        PointSnapManager.TOP_PRIORITY_POINT_STRATEGIES,
    );

    /** Viewer 实例。 */
    private readonly viewer: Viewer;
    /** 点捕捉策略映射表。 */
    private readonly strategyMap: Map<PointSnapStrategyType, AbstractPointSnapStrategy> = new Map();

    /** 指针捕捉使用的渲染器像素尺寸，避免高频拖动阶段重复创建向量。 */
    private readonly pointerSnapRendererSize = new THREE.Vector2();

    /** 指针捕捉使用的屏幕像素坐标，避免高频拖动阶段重复创建向量。 */
    private readonly pointerSnapScreenPosition = new THREE.Vector2();

    /** 当前模型捕捉请求复用的瞬态点数组。 */
    private readonly modelSnapTransientPointObjects: SketchPointObject[] = [];

    /** 当前模型捕捉请求复用的悬停门禁参数。 */
    private readonly visibleModelEdgeHoverOptions: VisibleModelEdgeHoverOptions = {
        modelGeometryEnabled: false,
        endpointEnabled: false,
        midpointEnabled: false,
        centerEnabled: false,
        quadrantEnabled: false,
    };

    /** 拖动捕捉当前高亮的目标边。 */
    private pointerSnapHighlightedEdge: THREE.Object3D | null = null;
    /** 全局捕捉是否激活。 */
    public isSnapEnabled: boolean = true;
    /** 是否允许可见模型边及其真实参考点进入草图捕捉链路。 */
    public isSnapToModelGeometryEnabled: boolean = false;
    /** 角度捕捉角度值（度）。 */
    public angleSnapDegree: number = 45;

    //------ 运行时快速捕捉状态 开始------
    /**
     * 运行时快速捕捉模式状态。
     * 说明：
     * 1. 键为 {@link QuickSnapMode} 枚举值；
     * 2. 同时最多只有一项为 true（单选互斥）；
     * 3. 全部为 false 表示当前无快速捕捉激活；
     * 4. 由工具栏直接写入，捕捉策略在 resolve 时读取。
     */
    public quickSnapState: Record<QuickSnapMode, boolean> = {
        [QuickSnapMode.Point]: false,
        [QuickSnapMode.Center]: false,
        [QuickSnapMode.Midpoint]: false,
        [QuickSnapMode.Quadrant]: false,
        [QuickSnapMode.Intersection]: false,
        [QuickSnapMode.Nearest]: false,
        [QuickSnapMode.Tangent]: false,
        [QuickSnapMode.HorizontalVertical]: false,
        [QuickSnapMode.Grid]: false,
    };

    /**
     * quickSnapMode 变更事件流（RxJS Subject）。
     * 说明：每次调用 {@link setQuickSnapMode} 后派发最新模式值（null 表示已关闭）。
     * UI 层订阅此流以同步工具栏选中状态，绘制逻辑订阅此流以触发 one-shot 清除。
     */
    private readonly _quickSnapModeChange$ = new Subject<QuickSnapMode | null>();

    /**
     * quickSnapMode 变更可观察对象（只读）。
     */
    public get quickSnapModeChange$(): Observable<QuickSnapMode | null> {
        return this._quickSnapModeChange$.asObservable();
    }

    /**
     * QuickSnapMode → PointSnapStrategyType 映射表。
     * 说明：快速捕捉模式路由时，根据此表查找对应策略，只执行该策略。
     */
    private static readonly QUICK_SNAP_STRATEGY_MAP: Readonly<Record<QuickSnapMode, PointSnapStrategyType>> = {
        [QuickSnapMode.Point]: PointSnapStrategyType.EndpointOrigin,
        [QuickSnapMode.Center]: PointSnapStrategyType.Center,
        [QuickSnapMode.Midpoint]: PointSnapStrategyType.Midpoint,
        [QuickSnapMode.Quadrant]: PointSnapStrategyType.Quadrant,
        [QuickSnapMode.Intersection]: PointSnapStrategyType.Intersection,
        [QuickSnapMode.Nearest]: PointSnapStrategyType.Nearest,
        [QuickSnapMode.Tangent]: PointSnapStrategyType.Tangent,
        [QuickSnapMode.HorizontalVertical]: PointSnapStrategyType.PointHorizontalVertical,
        [QuickSnapMode.Grid]: PointSnapStrategyType.Grid,
    };

    /**
     * 当前激活的快速捕捉模式，无激活时为 null。
     */
    public get quickSnapMode(): QuickSnapMode | null {
        const entry = Object.entries(this.quickSnapState).find(([, v]) => v);
        return entry ? (entry[0] as QuickSnapMode) : null;
    }

    /**
     * 设置快速捕捉模式（互斥单选），并派发变更事件。
     * 说明：
     * - 传入已激活的 mode 再次调用则清除（toggle 关闭）；
     * - 传入新 mode 则激活该项并关闭其他所有项；
     * - 传入 null 则清除所有激活状态。
     * @param mode 要激活的模式，null 表示全部关闭。
     */
    public setQuickSnapMode(mode: QuickSnapMode | null): void {
        const currentActive = this.quickSnapMode;
        // 先清空所有状态。
        for (const key of Object.keys(this.quickSnapState) as QuickSnapMode[]) {
            this.quickSnapState[key] = false;
        }
        // 若传入的 mode 与当前激活的相同，则视为 toggle 取消（已清除，不再重新激活）。
        if (mode !== null && mode !== currentActive) {
            this.quickSnapState[mode] = true;
        }
        // 派发变更事件，通知 UI 层和绘制逻辑。
        this._quickSnapModeChange$.next(this.quickSnapMode);
    }

    //------ 运行时快速捕捉状态 结束------


    /**
     * 构造函数。
     * @param viewer Viewer 实例。
     */
    public constructor(viewer: Viewer) {
        this.viewer = viewer;
        this.registerStrategy(new EndpointOriginPointSnapStrategy());
        this.registerStrategy(new CenterPointSnapStrategy());
        this.registerStrategy(new MidpointPointSnapStrategy());
        this.registerStrategy(new QuadrantPointSnapStrategy());
        this.registerStrategy(new IntersectionPointSnapStrategy());
        /**
         * 最近点捕捉在组合根装配当前启用的解析投影器。
         * 说明：
         * 1. 直线、完整圆与圆弧继续使用数学投影，保证基础解析几何的捕捉精度；
         * 2. 椭圆使用与强力裁剪、裁剪实体重建相同的权威参数几何服务；
         * 3. 抛物线、普通样条与样式样条暂不注册解析投影器，继续回退显示折线；
         * 4. 参数曲线只有在捕捉与后续编辑共享同一几何语义后才能进入运行时组合根。
         */
        this.registerStrategy(new NearestPointSnapStrategy([
            new SketchLineNearestPointProjector(),
            new SketchCircleFamilyNearestPointProjector(),
            new SketchEllipseNearestPointProjector(),
        ]));
        this.registerStrategy(new TangentPointSnapStrategy());
        this.registerStrategy(new PerpendicularPointSnapStrategy());
        this.registerStrategy(new ParallelPointSnapStrategy());
        this.registerStrategy(new HorizontalVerticalPointSnapStrategy());
        this.registerStrategy(new PointHorizontalVerticalPointSnapStrategy());
        this.registerStrategy(new LengthPointSnapStrategy());
        this.registerStrategy(new GridPointSnapStrategy());
        this.registerStrategy(new DynamicRulerPointSnapStrategy());
        this.registerStrategy(new AnglePointSnapStrategy());

        // 统一将 Viewer 注入所有已注册策略，避免策略内部单独持有引用。
        for (const strategy of this.strategyMap.values()) {
            strategy.setViewer(this.viewer);
        }
    }

    //------ 预览对象点捕捉过滤 开始------
    /**
     * 解析点捕捉链路可使用的 hoveredObject。
     * 说明：
     * 1. 绘制阶段的临时预览点、预览线、预览圆弧会参与场景渲染，但不属于正式草图几何；
     * 2. 若将这些预览对象继续传入点捕捉链路，会导致端点、最近端、中点、象限等提示错误显示；
     * 3. Convert Entities preview 会以独立 marker 进入场景树，也必须在这里统一排除；
     * 4. 因此这里统一将各类预览对象归一化为 null，确保后续策略与提示逻辑完全忽略它们。
     * @param hoveredObject 当前悬浮对象。
     * @returns 点捕捉链路可使用的悬浮对象；若命中绘制预览对象则返回 null。
     */
    public static resolvePointSnapHoveredObject(hoveredObject: THREE.Object3D | null): THREE.Object3D | null {
        // 先处理空值，避免运行时守卫对 null 做 Reflect.get 时抛出异常。
        if (hoveredObject === null) {
            return null;
        }

        // Convert Entities preview 使用独立 marker，与绘制态 preview 共享同一忽略语义。
        if (isSketchEntityReferencePreview(hoveredObject)) {
            return null;
        }

        // 绘制预览点仅用于视觉反馈，不应参与点捕捉。
        if (isSketchPointObject(hoveredObject) && hoveredObject.isDrawingPreviewPoint) {
            return null;
        }

        // 绘制预览线仅用于视觉反馈，不应参与点捕捉。
        if (isSketchLineObject(hoveredObject) && hoveredObject.isDrawingPreviewLine) {
            return null;
        }

        // 绘制预览圆弧仅用于视觉反馈，不应参与点捕捉。
        if (isSketchArcObject(hoveredObject) && hoveredObject.isDrawingPreviewArc) {
            return null;
        }

        return hoveredObject;
    }

    /**
     * 过滤点捕捉候选对象中的绘制预览对象。
     * 说明：
     * 1. 某些调用链会把候选对象整体传入点捕捉管理器；
     * 2. 即使 hoveredObject 已经过滤，候选集里若混入 Convert preview 或绘制态预览，仍可能被策略误用；
     * 3. 因此这里统一做第二层过滤，确保预览对象永远不参与捕捉。
     * @param candidateObjects 原始候选对象列表。
     * @returns 已排除预览对象的候选对象列表。
     */
    public static resolvePointSnapCandidateObjects(candidateObjects: THREE.Object3D[]): THREE.Object3D[] {
        return candidateObjects.filter((candidateObject) => {
            return PointSnapManager.resolvePointSnapHoveredObject(candidateObject) !== null;
        });
    }
    //------ 预览对象点捕捉过滤 结束------

    /**
     * 注册点捕捉策略。
     * @param strategy 点捕捉策略实例。
     */
    public registerStrategy(strategy: AbstractPointSnapStrategy): void {
        this.strategyMap.set(strategy.strategyType, strategy);
    }

    /**
     * 获取指定类型的点捕捉策略。
     * @param strategyType 策略类型。
     * @returns 点捕捉策略实例。
     * @throws 当指定类型的策略尚未注册时抛出错误。
     */
    public getStrategy(strategyType: PointSnapStrategyType): AbstractPointSnapStrategy {
        const strategy = this.strategyMap.get(strategyType);
        if (!strategy) {
            throw new Error(`未找到点捕捉策略：${strategyType}`);
        }
        return strategy;
    }

    /**
     * 获取全部点捕捉策略。
     * @returns 点捕捉策略列表。
     */
    public getStrategies(): AbstractPointSnapStrategy[] {
        return Array.from(this.strategyMap.values());
    }

    /**
     * 设置指定策略是否启用。
     * @param strategyType 策略类型。
     * @param enabled 是否启用。
     */
    public setStrategyEnabled(strategyType: PointSnapStrategyType, enabled: boolean): void {
        const strategy = this.getStrategy(strategyType);
        strategy.setEnabled(enabled);
    }

    /**
     * 判断指定策略是否启用。
     * @param strategyType 策略类型。
     * @returns 是否启用。
     */
    public isStrategyEnabled(strategyType: PointSnapStrategyType): boolean {
        return this.getStrategy(strategyType).enabled;
    }

    /**
     * 执行点捕捉求解。
     * 说明：
     * 1. 全局捕捉关闭时直接返回，不允许普通、最高优先级或快速捕捉绕过配置；
     * 2. candidateObjects 为非点类型策略（如交叉点、靠近点等）保留的候选对象来源；
     *    点类型策略已直接从 SketchGroup 与会话瞬时参考点集合获取，不依赖此字段；
     * 3. 快速捕捉模式激活时，只调用对应策略，其余全部跳过；
     * 4. 普通模式下向所有已启用策略并行发起求解，收集全部命中结果；
     * 5. 按 weight 取权重最高的结果返回，保证高优先级策略（如端点）胜出。
     * @param request 点捕捉请求参数。
     * @returns 权重最高的点捕捉结果；若没有命中则返回 null。
     */
    public resolve(request: PointSnapRequest): PointSnapResult | null {
        // 全局配置是所有捕捉语义的唯一总门禁，必须早于快速捕捉和最高优先级点策略判断。
        if (!this.isSnapEnabled) {
            this.viewer.sketchSessionReferencePointManager?.clearVisibleModelEdgeHover();
            this.viewer.sketchSessionReferencePointManager?.clearModelPointAxisTracking();
            return null;
        }

        /*
         * 模型参考点必须先完成“可见投影边 BVH 命中”再进入点策略。调用方构造请求时可能仍携带
         * 上一帧模型点，因此这里剔除旧瞬态点，并只附加当前唤醒源边的可捕捉参考点。
         */
        const sessionReferencePointManager = this.viewer.sketchSessionReferencePointManager;
        const resolvedHoveredObject = PointSnapManager.resolvePointSnapHoveredObject(
            request.hoveredObject ?? null,
        );
        const formalSketchEdgeTakesOwnership = isAbstractSketchEdgeObject(resolvedHoveredObject)
            && !PointSnapManager.isRequestTargetExcluded(request, resolvedHoveredObject);
        this.visibleModelEdgeHoverOptions.modelGeometryEnabled = this.isSnapToModelGeometryEnabled;
        this.visibleModelEdgeHoverOptions.endpointEnabled = this.getStrategy(
            PointSnapStrategyType.EndpointOrigin,
        ).enabled;
        this.visibleModelEdgeHoverOptions.midpointEnabled = this.getStrategy(
            PointSnapStrategyType.Midpoint,
        ).enabled;
        this.visibleModelEdgeHoverOptions.centerEnabled = this.getStrategy(
            PointSnapStrategyType.Center,
        ).enabled;
        this.visibleModelEdgeHoverOptions.quadrantEnabled = this.getStrategy(
            PointSnapStrategyType.Quadrant,
        ).enabled;
        // 轴向候选与移入显示分别管理，即使正式草图边接管 hover 仍可查询模型投影点。
        sessionReferencePointManager?.configureModelPointAxisTracking(this.visibleModelEdgeHoverOptions);
        let visibleModelReferencePoints: readonly SketchPointObject[] = [];
        if (formalSketchEdgeTakesOwnership) {
            // SOLIDWORKS 只保留当前唤醒实体的动态点；正式草图边接管后，旧模型投影边必须退出。
            sessionReferencePointManager?.clearVisibleModelEdgeHover();
        } else {
            visibleModelReferencePoints = sessionReferencePointManager?.updateVisibleModelEdgeHover(
                request.screenPosition,
                this.visibleModelEdgeHoverOptions,
            ) ?? [];
        }
        this.modelSnapTransientPointObjects.length = 0;
        for (const point of request.transientPointObjects ?? []) {
            if (
                !point.isSketchSessionModelEdgeEndpoint
                && !point.isSketchSessionModelEdgeMidpoint
                && !point.isSketchSessionModelEdgeCenter
                && !point.isSketchSessionModelEdgeQuadrant
            ) {
                this.modelSnapTransientPointObjects.push(point);
            }
        }
        this.modelSnapTransientPointObjects.push(...visibleModelReferencePoints);

        const candidateObjects = PointSnapManager.resolvePointSnapCandidateObjects(
            request.candidateObjects.length > 0
                ? request.candidateObjects
                : this.collectCandidateObjects(),
        ).filter((candidateObject) => {
            return !PointSnapManager.isRequestTargetExcluded(request, candidateObject);
        });
        // 若调用方未提供候选对象，从活跃草图组自动收集。
        const enrichedRequest: PointSnapRequest = {
            ...request,
            candidateObjects,
            transientPointObjects: this.modelSnapTransientPointObjects,
            //------ 预览线点捕捉过滤 开始------
            // 绘制预览线只作为视觉反馈存在，不允许进入点捕捉判定链路。
            hoveredObject: PointSnapManager.isRequestTargetExcluded(request, resolvedHoveredObject)
                ? null
                : resolvedHoveredObject,
            //------ 预览线点捕捉过滤 结束------
            // 注入当前快速捕捉模式，策略内部据此调整行为。
            quickSnapMode: this.quickSnapMode,
        };

        //------ 快速捕捉路由 开始------
        const activeQuickMode = this.quickSnapMode;
        if (activeQuickMode !== null) {
            // 快速捕捉模式：只调用对应策略，禁用所有其他策略参与。
            const strategyType = PointSnapManager.QUICK_SNAP_STRATEGY_MAP[activeQuickMode];
            const strategy = this.strategyMap.get(strategyType);
            if (!strategy) {
                return null;
            }
            return strategy.resolve(enrichedRequest);
        }
        //------ 快速捕捉路由 结束------

        //------ 点类捕捉全局最高优先级 开始------
        // 统一收口：端点/中点/中心点/象限点属于最强语义点命中。
        // 只要这四类任一命中，就直接在这组内部决策并返回，
        // 不再让平行/垂直/HV/切线/角度等高 weight 策略抢占结果。
        const topPriorityPointResult = this.resolveTopPriorityPointResult(enrichedRequest);
        if (topPriorityPointResult !== null) {
            return topPriorityPointResult;
        }
        //------ 点类捕捉全局最高优先级 结束------

        const candidates: PointSnapResult[] = [];

        for (const [strategyType, strategy] of this.strategyMap.entries()) {
            // 全局门禁已在入口收口，此处只判断具体捕捉项是否启用。
            if (!strategy.enabled) {
                continue;
            }

            // 最高优先级点类策略已在上方统一求解，普通竞争阶段不再重复执行。
            if (PointSnapManager.TOP_PRIORITY_POINT_STRATEGY_SET.has(strategyType)) {
                continue;
            }

            const result = strategy.resolve(enrichedRequest);
            if (result !== null) {
                candidates.push(result);
            }
        }

        if (candidates.length === 0) {
            return null;
        }

        // 返回权重最高的命中结果。
        return candidates.reduce((best, current) =>
            current.weight > best.weight ? current : best,
        );
    }

    /**
     * 根据浏览器指针坐标执行一次完整点捕捉交互。
     *
     * 该入口统一服务拖点、移动实体和复制实体等非绘制命令，负责把浏览器坐标转换为
     * 捕捉策略使用的渲染器像素坐标，并同步右下角捕捉图标。活动草图会话的原点和
     * 模型投影点也在此统一注入，保证移动实体、复制实体与拖点使用同一份会话参考点。
     * 该入口只返回坐标候选，绝不根据 `suggestedRelationKind` 创建关系，关系是否提交仍由
     * 具体命令的独立事务决定。
     *
     * @param clientX 浏览器视口 X 坐标。
     * @param clientY 浏览器视口 Y 坐标。
     * @param worldPosition 指针在当前草图参考面上的自由投影点。
     * @param excludedTargetObjectIds 本次交互禁止作为捕捉目标的对象 ID 集合。
     * @param directionalReferencePoints 水平/竖直捕捉使用的非绘制方向参考点集合。
     * @returns 最优捕捉结果；捕捉不可用或未命中时返回 null。
     */
    public resolvePointerSnap(
        clientX: number,
        clientY: number,
        worldPosition: THREE.Vector3,
        excludedTargetObjectIds?: ReadonlySet<string>,
        directionalReferencePoints?: readonly THREE.Vector3[],
    ): PointSnapResult | null {
        // From 起点、To 目标点和拖点都复用该入口；关闭全局捕捉时不得继续拾取或高亮候选边。
        if (!this.isSnapEnabled) {
            this.clearPointerSnapEdgeHighlight();
            this.hidePointerSnapIndicator();
            return null;
        }

        const renderer = this.viewer.renderer;
        const domElement = renderer?.domElement;
        if (!renderer || !domElement) {
            this.clearPointerSnapEdgeHighlight();
            this.hidePointerSnapIndicator();
            return null;
        }

        const rect = domElement.getBoundingClientRect();
        if (rect.width <= 0 || rect.height <= 0) {
            this.clearPointerSnapEdgeHighlight();
            this.hidePointerSnapIndicator();
            return null;
        }

        // 捕捉策略的阈值以渲染器像素为单位，不能直接使用 CSS client 坐标。
        renderer.getSize(this.pointerSnapRendererSize);
        this.pointerSnapScreenPosition.set(
            ((clientX - rect.left) / rect.width) * this.pointerSnapRendererSize.x,
            ((clientY - rect.top) / rect.height) * this.pointerSnapRendererSize.y,
        );

        const selectionManager = this.viewer.entitySelectionManager;
        const pointerEdgePicker = selectionManager?.pickActiveSketchPointSnapEdgeAt;
        const hoveredObject = typeof pointerEdgePicker === 'function'
            ? pointerEdgePicker.call(
                selectionManager,
                clientX,
                clientY,
                excludedTargetObjectIds,
            )
            : selectionManager?.hoveredObject ?? null;
        this.syncPointerSnapEdgeHighlight(hoveredObject);

        let result = this.resolve({
            screenPosition: this.pointerSnapScreenPosition,
            worldPosition,
            candidateObjects: [],
            hoveredObject,
            drawingFirstPoint: null,
            directionalReferencePoints,
            referenceDirection: null,
            // 会话参考点不属于正式草图组，必须通过显式白名单进入非绘制捕捉链路。
            transientPointObjects: this.viewer.sketchSessionReferencePointManager?.getPointObjects() ?? [],
            excludedTargetObjectIds,
        });

        // 普通轴向策略以活动状态输出落点；移动/复制/拖点也必须消费它，而不只画追踪线。
        // 精确点类与快速捕捉维持原优先级，不能被普通轴向结果覆盖。
        const pointAxisStrategy = this.getStrategy(PointSnapStrategyType.PointHorizontalVertical);
        if (this.quickSnapMode === null && pointAxisStrategy.enabled
            && pointAxisStrategy instanceof PointHorizontalVerticalPointSnapStrategy
            && (!result || !PointSnapManager.TOP_PRIORITY_POINT_STRATEGY_SET.has(result.strategyType))) {
            const axisState = pointAxisStrategy.getActiveState();
            if (axisState.snapPoint && (axisState.hasHorizontal || axisState.hasVertical)) {
                result = {
                    strategyType: PointSnapStrategyType.PointHorizontalVertical,
                    position: axisState.snapPoint,
                    indicatorPosition: axisState.snapPoint,
                    targetObject: null,
                    weight: 0,
                    indicatorIconOverride: axisState.hasHorizontal
                        ? '/textures/sketch/snap-horizontal.svg' : '/textures/sketch/snap-vertical.svg',
                };
            }
        }

        if (!result) {
            this.hidePointerSnapIndicator();
            return null;
        }

        // 策略可选择仅显示几何预览而不显示 CSS 图标，例如中点的边缘悬停阶段。
        if (result.showIndicator !== false) {
            this.viewer.pointerNearbyLabelManager?.showPointSnapIndicator(result);
        } else {
            this.hidePointerSnapIndicator();
        }
        return result;
    }

    /**
     * 构造移动域的点捕捉排除 ID 集合。
     *
     * 除普通对象树外，草图复合对象还可能通过 `relatedSketchPoints`、`collectSet` 或
     * `getEdges()` 维护非父子关系的控制点与边。这里统一递归收集并用 UUID 去重，确保拖动
     * 点、Move 和 Copy 都不会吸附到本次正在移动的自身几何。
     *
     * @param objects 当前正在移动或预览的根对象集合。
     * @returns 包含对象树与草图关联域的只读 ID 集合。
     */
    public static createExcludedTargetObjectIds(
        objects: readonly THREE.Object3D[],
    ): ReadonlySet<string> {
        const excludedIds = new Set<string>();
        objects.forEach((object) => {
            PointSnapManager.collectExcludedTargetObjectIds(object, excludedIds);
        });
        return excludedIds;
    }

    /**
     * 使用当前管理器构造移动域捕捉排除集合。
     * @param objects 当前正在移动或预览的根对象集合。
     * @returns 包含对象树与草图关联域的只读 ID 集合。
     */
    public createExcludedTargetObjectIds(
        objects: readonly THREE.Object3D[],
    ): ReadonlySet<string> {
        return PointSnapManager.createExcludedTargetObjectIds(objects);
    }

    /**
     * 清理一次指针捕捉交互产生的全部反馈。
     *
     * 拖动停止、Ctrl 临时抑制或命令取消时都必须同时隐藏 CSS 图标并清理策略级引导线、
     * 中点、象限点和高亮状态，避免瞬态对象残留到下一次交互。
     */
    public clearPointerSnapFeedback(): void {
        this.hidePointerSnapIndicator();
        this.clearPointerSnapEdgeHighlight();
        this.clearAllPreviews();
    }

    /**
     * 在普通求解前，先判断全局最高优先级的点类策略是否已经命中。
     * 说明：
     * 1. 端点、中点、中心点、象限点属于用户最明确的几何点语义；
     * 2. 只要这组里任意一个命中，就应当直接胜出；
     * 3. 若这组没有命中，再回退到普通全策略竞争流程。
     * @param request 已补全的点捕捉请求。
     * @returns 点类优先结果；无命中时返回 null。
     */
    private resolveTopPriorityPointResult(request: PointSnapRequest): PointSnapResult | null {
        const pointCandidates: PointSnapResult[] = [];
        for (const strategyType of PointSnapManager.TOP_PRIORITY_POINT_STRATEGIES) {
            const strategy = this.strategyMap.get(strategyType);
            if (!strategy || !strategy.enabled) {
                continue;
            }

            const result = strategy.resolve(request);
            if (result !== null) {
                pointCandidates.push(result);
            }
        }

        if (pointCandidates.length === 0) {
            return null;
        }

        // 点类策略一旦命中，立即冻结并清理其他非点类策略的预览和活动状态，
        // 避免上一帧残留的 H/V、平行、垂直等语义继续参与当前帧 UI 表现。
        this.freezeNonPointStrategies();

        return pointCandidates.reduce((best, current) =>
            current.weight > best.weight ? current : best,
        );
    }

    /**
     * 冻结非最高优先级点类策略。
     * 说明：
     * 1. 当端点/中点/中心点/象限点任一命中时，其它捕捉语义必须退出当前帧竞争；
     * 2. 这里统一调用 clearPreview，清除引导线与活动状态，避免残留 UI 干扰点类主语义。
     */
    private freezeNonPointStrategies(): void {
        for (const [strategyType, strategy] of this.strategyMap.entries()) {
            if (PointSnapManager.TOP_PRIORITY_POINT_STRATEGY_SET.has(strategyType)) {
                continue;
            }

            strategy.clearPreview();
        }
    }

    /**
     * 从当前活跃草图组的已维护集合中收集所有候选草图对象。
     * 说明：
     * 1. 直接读取 SketchGroup 自动维护的 _sketchObjectSet，无需再 traverse 子树；
     * 2. 若没有活跃草图组则返回空数组。
     * @returns 候选对象列表。
     */
    private collectCandidateObjects(): THREE.Object3D[] {
        const group = this.viewer.sketchGroupManager?.activeSketchGroup;
        if (!group) {
            return [];
        }
        // 直接从草图组维护的集合中获取，避免 traverse 遍历整个子树。
        return Array.from(group.getSketchObjects());
    }

    //------ 移动复制预览捕捉排除 开始------
    /**
     * 判断对象是否被当前捕捉请求排除。
     *
     * @param request 点捕捉请求。
     * @param targetObject 待判断对象。
     * @returns 对象在排除集合中返回 true。
     */
    private static isRequestTargetExcluded(
        request: PointSnapRequest,
        targetObject: THREE.Object3D | null | undefined,
    ): boolean {
        if (!targetObject || !request.excludedTargetObjectIds) {
            return false;
        }
        return request.excludedTargetObjectIds.has(targetObject.uuid);
    }

    /**
     * 递归收集一个对象及其草图关联域 ID。
     * @param object 当前对象。
     * @param excludedIds 正在构造的排除 ID 集合。
     */
    private static collectExcludedTargetObjectIds(
        object: THREE.Object3D,
        excludedIds: Set<string>,
    ): void {
        if (excludedIds.has(object.uuid)) {
            return;
        }
        excludedIds.add(object.uuid);

        // Three.js 父子树中的控制点和辅助对象直接进入排除域。
        object.children.forEach((child) => {
            PointSnapManager.collectExcludedTargetObjectIds(child, excludedIds);
        });

        // 草图边通常通过 relatedSketchPoints 持有控制点，而不是把控制点作为子对象。
        const relatedSketchPoints = Reflect.get(object, 'relatedSketchPoints');
        if (relatedSketchPoints instanceof Set) {
            relatedSketchPoints.forEach((relatedObject) => {
                if (PointSnapManager.isObject3DLike(relatedObject)) {
                    PointSnapManager.collectExcludedTargetObjectIds(relatedObject, excludedIds);
                }
            });
        }

        // 集合对象通过 collectSet 维护外框成员，必须与集合本身一起排除。
        const collectSet = Reflect.get(object, 'collectSet');
        if (collectSet instanceof Set) {
            collectSet.forEach((sourceObject) => {
                if (PointSnapManager.isObject3DLike(sourceObject)) {
                    PointSnapManager.collectExcludedTargetObjectIds(sourceObject, excludedIds);
                }
            });
        }

        // 部分复合对象仅通过 getEdges() 暴露真实边集合，统一纳入当前移动域。
        const getEdges = Reflect.get(object, 'getEdges');
        if (typeof getEdges === 'function') {
            const edges = getEdges.call(object);
            if (Array.isArray(edges)) {
                edges.forEach((edge) => {
                    if (PointSnapManager.isObject3DLike(edge)) {
                        PointSnapManager.collectExcludedTargetObjectIds(edge, excludedIds);
                    }
                });
            }
        }
    }

    /**
     * 判断未知值是否为可递归收集的 Three.js 对象。
     * @param value 待判断值。
     * @returns 值具备 Object3D 基础结构时返回 true。
     */
    private static isObject3DLike(value: unknown): value is THREE.Object3D {
        return typeof value === 'object'
            && value !== null
            && typeof (value as THREE.Object3D).uuid === 'string'
            && Array.isArray((value as THREE.Object3D).children);
    }

    /** 隐藏指针右下角的捕捉图标。 */
    private hidePointerSnapIndicator(): void {
        this.viewer.pointerNearbyLabelManager?.hideLabel(PointerNearbyLabelPosition.RightBottom);
    }

    /**
     * 将精确拾取命中的草图边同步为拖动期间的手动 Hover 状态。
     *
     * 活动拖动会短路 EntitySelectionManager 的普通 hover 更新，因此必须显式把同源拾取结果
     * 交给 InteractionStateManager；后者会在普通草图视觉刷新中消费该提示并应用绘制态同款材质。
     *
     * @param hoveredObject 当前精确拾取对象。
     */
    private syncPointerSnapEdgeHighlight(hoveredObject: THREE.Object3D | null): void {
        const nextEdge = hoveredObject
            && Reflect.get(hoveredObject, 'isAbstractSketchEdgeObject') === true
            ? hoveredObject
            : null;
        if (this.pointerSnapHighlightedEdge === nextEdge) {
            return;
        }

        this.clearPointerSnapEdgeHighlight();
        if (!nextEdge) {
            return;
        }

        this.viewer.interactionStateManager?.setSketchObjectState(
            nextEdge,
            GeometryInteractionState.Hover,
        );
        this.pointerSnapHighlightedEdge = nextEdge;
    }

    /** 恢复拖动捕捉临时高亮的目标边。 */
    private clearPointerSnapEdgeHighlight(): void {
        if (!this.pointerSnapHighlightedEdge) {
            return;
        }
        this.viewer.interactionStateManager?.restoreSketchObjectState(this.pointerSnapHighlightedEdge);
        this.pointerSnapHighlightedEdge = null;
    }
    //------ 移动复制预览捕捉排除 结束------

    /**
     * 广播清除所有策略的预览状态。
     * 说明：在绘制结束或工具切换时调用，通知所有策略清理其管理的场景对象（引导线等）。
     */
    public clearAllPreviews(): void {
        for (const strategy of this.strategyMap.values()) {
            strategy.clearPreview();
        }
        this.viewer.sketchSessionReferencePointManager?.clearVisibleModelEdgeHover();
    }

    /**
     * 销毁点捕捉管理器，释放指示器资源。
     */
    public dispose(): void {
        // 完成事件流，释放所有订阅。
        this._quickSnapModeChange$.complete();
    }

    //------ 捕捉设置更新 开始------
    /**
     * 根据编辑器设置面板输出的快照同步更新所有捕捉策略状态。
     * 说明：
     * 1. 先更新全局激活标记和角度值；
     * 2. 再遍历映射表，逐项设置各策略启用状态；
     * 3. DynamicRuler 策略不在设置面板中暴露，始终保持启用。
     * @param settings 来自编辑器设置面板的捕捉配置快照。
     */
    public updateSettings(settings: PointSnapSettings): void {
        // 更新全局激活状态。
        this.isSnapEnabled = settings.isSketchSnapEnabled;
        this.isSnapToModelGeometryEnabled = settings.isSnapToModelGeometryEnabled ?? false;
        if (!this.isSnapEnabled || !this.isSnapToModelGeometryEnabled) {
            // 配置切换发生在交互过程中时，立即清除旧的捕捉图标、高亮、动态模型点和策略预览。
            this.clearPointerSnapFeedback();
        }
        // 更新角度捕捉角度值。
        this.angleSnapDegree = settings.angleSnapDegree;

        // 直接遍历 snapOptionState，键已是 PointSnapStrategyType，无需转换。
        for (const [strategyTypeKey, optionEnabled] of Object.entries(settings.snapOptionState)) {
            // 跳过未在策略映射中注册的键（防御性守卫，防止外部传入脏数据）。
            if (!this.strategyMap.has(strategyTypeKey as PointSnapStrategyType)) {
                continue;
            }
            // 全局捕捉关闭时所有子策略均禁用；否则按各项开关决定。
            const enabled = settings.isSketchSnapEnabled && (optionEnabled ?? false);
            this.setStrategyEnabled(strategyTypeKey as PointSnapStrategyType, enabled);
        }

    }
    //------ 捕捉设置更新 结束------
}
